/**
 * 中央ラベルを核にした放射状クラスタ配置。
 *
 * HOME の待機ショット雲（WaitingShotCloud、中央ラベル = 鋭意制作中 /
 * レビュー待ち）で使う。
 * 核の矩形からチップを放射状に外へ向かって詰めていく。コメント雲の帯状配置
 * （commentCloudLayout）がカード矩形の左右バンドを前提とするのに対し、
 * こちらはカードの大きさに依存せず「ラベルの周りに寄る」ことを優先する。
 * ビューポートが大きくてもチップは中央のクラスタに留まり、余白だけが広がる。
 *
 * 要件:
 *  - id ハッシュ + 黄金角シーケンスで決定的に配置する（再レンダリングや
 *    データ再取得で踊らない）。
 *  - チップ同士・ラベルとは重ねない。bounds からはみ出さない。
 *  - 置けないチップが 1 つでもあれば null を返す（呼び出し側が件数を
 *    減らして再試行し、収まる形だけを表示する）。
 *
 * DOM 非依存の純粋関数なので vitest で直接検証できる。
 */

import {
  hashString,
  mulberry32,
  type CloudBubblePlacement,
  type CloudRect,
} from "./commentCloudLayout";

export interface ClusterChipSpec {
  /** 配置の一意キー（ショット ID）。ハッシュ配置のシード。 */
  id: string;
  /** チップの推定幅(px)。 */
  width: number;
  /** チップの推定高さ(px)。 */
  height: number;
}

export interface ClusterLayoutOptions {
  /** チップ間・ラベルとの最小間隔(px)。既定: 12。 */
  gap?: number;
  /** 半径の探索刻み(px)。既定: 10。 */
  radiusStep?: number;
  /** 横方向の広がり係数（楕円）。ワイド画面向けに既定 1.4。 */
  spreadX?: number;
}

/** 黄金角(rad)。周方向へ均等に散らすための増分。 */
const GOLDEN_ANGLE = 2.399963229728653;

/** 第一候補の方向が詰まったとき左右へ振る候補角。 */
const ANGLE_OFFSETS: readonly number[] = [0, 0.7, -0.7, 1.4, -1.4];

const intersects = (a: CloudRect, b: CloudRect, gap: number): boolean =>
  a.left < b.left + b.width + gap &&
  b.left < a.left + a.width + gap &&
  a.top < b.top + b.height + gap &&
  b.top < a.top + a.height + gap;

const inside = (r: CloudRect, bounds: CloudRect): boolean =>
  r.left >= bounds.left &&
  r.top >= bounds.top &&
  r.left + r.width <= bounds.left + bounds.width &&
  r.top + r.height <= bounds.top + bounds.height;

/**
 * 1 チップ分の配置探索。黄金角 × angleIndex + id ハッシュ由来のゆらぎで
 * 方向を決め、核の縁から半径を radiusStep 刻みで外へ広げながら、
 * 「bounds 内」「ラベル・既配置チップと非交差」を満たす最初の位置を返す。
 */
const findChipPlacement = (
  chip: ClusterChipSpec,
  angleIndex: number,
  occupied: readonly CloudBubblePlacement[],
  labelRect: CloudRect,
  bounds: CloudRect,
  gap: number,
  radiusStep: number,
  spreadX: number,
): CloudBubblePlacement | null => {
  const centerX = labelRect.left + labelRect.width / 2;
  const centerY = labelRect.top + labelRect.height / 2;
  const startRadius = Math.min(labelRect.width, labelRect.height) / 2;
  const maxRadius = Math.hypot(bounds.width, bounds.height) / 2 + startRadius;

  const rng = mulberry32(hashString(chip.id));
  // 黄金角で方向を均等に散らし、ハッシュ由来のゆらぎでグリッド感を消す
  const baseAngle = angleIndex * GOLDEN_ANGLE + (rng() - 0.5) * 0.9;

  for (let r = startRadius; r <= maxRadius; r += radiusStep) {
    for (const offset of ANGLE_OFFSETS) {
      const angle = baseAngle + offset;
      const candidate: CloudBubblePlacement = {
        id: chip.id,
        left: centerX + Math.cos(angle) * r * spreadX - chip.width / 2,
        top: centerY + Math.sin(angle) * r - chip.height / 2,
        width: chip.width,
        height: chip.height,
      };
      if (!inside(candidate, bounds)) continue;
      if (intersects(candidate, labelRect, gap)) continue;
      if (occupied.some((p) => intersects(candidate, p, gap))) continue;
      return candidate;
    }
  }
  return null;
};

/**
 * チップ群の配置を計算する。
 *
 * 手順（チップごと、配列順）:
 *  1. 方向: 黄金角 × 配列位置 + id ハッシュ由来のゆらぎ。
 *  2. 半径: ラベル矩形の縁から radiusStep 刻みで外へ広げながら、
 *     「bounds 内」「ラベル・既配置チップと非交差」を満たす最初の位置を探す。
 *     第一候補の方向が詰まっている場合は左右へ振った候補角も試す。
 *  3. 見つからないチップがあれば null（呼び出し側で件数を減らす）。
 */
export function layoutLabelClusterChips(
  chips: ClusterChipSpec[],
  labelRect: CloudRect,
  bounds: CloudRect,
  options?: ClusterLayoutOptions,
): CloudBubblePlacement[] | null {
  if (chips.length === 0) return [];

  const gap = options?.gap ?? 12;
  const radiusStep = options?.radiusStep ?? 10;
  const spreadX = options?.spreadX ?? 1.4;

  const placements: CloudBubblePlacement[] = [];

  for (let i = 0; i < chips.length; i += 1) {
    const placed = findChipPlacement(
      chips[i],
      i,
      placements,
      labelRect,
      bounds,
      gap,
      radiusStep,
      spreadX,
    );
    if (!placed) return null;
    placements.push(placed);
  }

  return placements;
}

export default layoutLabelClusterChips;
